import * as React from 'react';
import {
  storyblokEditable,
  StoryblokComponent,
  StoryblokRichText,
  type SbBlokData,
} from '@storyblok/react';

import type { MediaTextStoryblok } from '../../types/storyblok';

interface MediaTextProps {
  blok: MediaTextStoryblok;
}

type Alignment = 'left' | 'right' | 'left_detached' | 'right_detached';
type Overlay = 'none' | 'gradient';
type Background = 'default' | 'muted' | 'accent';

const backgroundClass: Record<Background, string> = {
  default: '',
  muted: 'bg-gray-50',
  accent: 'bg-secondary-iceBlue',
};

// Rounding varies by viewport and alignment type:
// Glued variants (left/right):
// - Mobile: all corners 8px
// - Desktop 1024-1440px: inner edge only 64px (glued to outer edge)
// - Wide >1440px: all corners 64px (detached in centered container)
// Detached variants (left_detached/right_detached):
// - Always all corners rounded 16px (never glues to edge)
const mediaRoundingClass: Record<Alignment, string> = {
  left: 'rounded-[8px] desktop:rounded-none desktop:rounded-r-[64px] min-[1441px]:rounded-[64px]',
  right: 'rounded-[8px] desktop:rounded-none desktop:rounded-l-[64px] min-[1441px]:rounded-[64px]',
  left_detached: 'rounded-[16px]',
  right_detached: 'rounded-[16px]',
};

// Image size: glued variants use 599px, detached variants use 464px
const isDetached = (alignment: Alignment): boolean =>
  alignment === 'left_detached' || alignment === 'right_detached';

// Get the base direction (left or right) from alignment
const getDirection = (alignment: Alignment): 'left' | 'right' =>
  alignment === 'left' || alignment === 'left_detached' ? 'left' : 'right';

const normaliseAlignment = (value: MediaTextStoryblok['media_alignment']): Alignment => {
  if (value === 'left' || value === 'right' || value === 'left_detached' || value === 'right_detached') {
    return value;
  }
  return 'right';
};

const normaliseOverlay = (value: MediaTextStoryblok['media_overlay']): Overlay =>
  value === 'gradient' ? 'gradient' : 'none';

const normaliseBackground = (value: MediaTextStoryblok['background']): Background =>
  value === 'muted' || value === 'accent' ? value : 'default';

const resolveCtaHref = (link: MediaTextStoryblok['cta_link']): string | null => {
  if (!link) return null;
  const href = link.cached_url || link.url;
  return href ? href : null;
};

// Use the Storyblok asset URL as-is. The project's existing pattern (header
// and footer logos) consumes asset.filename directly without invoking the
// /m/<size>/ image transformer. If a future spec needs CDN-side resizing,
// extract the rewrite into a util that handles both s3.amazonaws.com/ and
// a.storyblok.com/ origins.
const buildSrc = (asset: MediaTextStoryblok['media']): string | undefined =>
  asset?.filename || undefined;

const GRADIENT =
  'linear-gradient(150.59deg, rgba(47,143,232,0.4) 12.765%, rgba(0,29,136,0.4) 81.414%)';

export const MediaText: React.FC<MediaTextProps> = ({ blok }) => {
  const headlineId = React.useId();
  const alignment = normaliseAlignment(blok.media_alignment);
  const overlay = normaliseOverlay(blok.media_overlay);
  const background = normaliseBackground(blok.background);
  const ctaHref = resolveCtaHref(blok.cta_link);
  const ctaLabel = blok.cta_label?.trim();
  const showCta = Boolean(ctaHref && ctaLabel);
  const mediaSrc = buildSrc(blok.media);
  const mediaAlt = blok.media?.alt ?? '';

  const sectionClass = ['py-10 desktop:py-16', backgroundClass[background]]
    .filter(Boolean)
    .join(' ');

  const detached = isDetached(alignment);
  const direction = getDirection(alignment);

  // Desktop: row layout based on direction
  // Mobile: column layout with image on top centered
  const flexDirection =
    direction === 'left' ? 'desktop:flex-row' : 'desktop:flex-row-reverse';

  // Detached variants: always use symmetric padding (standard SiteContainer behavior)
  // Glued variants: at desktop (1024-1440px), no padding on image side; at >1440px, both sides
  const containerPaddingClass = detached
    ? 'desktop:px-grid-margin-desktop'
    : direction === 'left'
      ? 'desktop:pl-0 desktop:pr-grid-margin-desktop min-[1441px]:px-grid-margin-desktop'
      : 'desktop:pr-0 desktop:pl-grid-margin-desktop min-[1441px]:px-grid-margin-desktop';

  // Text column width: glued uses 461px, detached uses 539px
  const textWidthClass = detached ? 'desktop:w-[539px]' : 'desktop:w-[461px]';

  // Image size: glued uses 599px, detached uses 464px
  const imageSizeClass = detached
    ? 'w-[232px] h-[232px] desktop:w-[464px] desktop:h-[464px]'
    : 'w-[232px] h-[232px] desktop:w-[599px] desktop:h-[599px]';

  // Flex justify: detached centers content with gap, glued spreads to edges
  const justifyClass = detached
    ? 'desktop:justify-center desktop:gap-[107px]'
    : 'desktop:justify-between';

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      aria-labelledby={headlineId}
      className={sectionClass}
    >
      {/* Custom container: max-w-site centered, with asymmetric padding at desktop */}
      <div
        className={`max-w-site mx-auto px-grid-margin-mobile ${containerPaddingClass}`}
      >
        <div
          className={`flex flex-col items-center gap-8 ${flexDirection} desktop:items-center ${justifyClass}`}
        >
          {/* Media - centered square on mobile, fixed size on desktop */}
          <div
            className={`relative ${imageSizeClass} shrink-0 shadow-[0_4px_64px_0_rgba(47,143,232,0.15)] overflow-hidden ${mediaRoundingClass[alignment]}`}
          >
            {mediaSrc && (
              <img
                src={mediaSrc}
                alt={mediaAlt}
                width={599}
                height={599}
                className={`w-full h-full object-cover ${mediaRoundingClass[alignment]}`}
              />
            )}
            {overlay === 'gradient' && (
              <div
                aria-hidden="true"
                className={`absolute inset-0 pointer-events-none ${mediaRoundingClass[alignment]}`}
                style={{ backgroundImage: GRADIENT }}
              />
            )}
          </div>

          {/* Text column - centered on mobile, left-aligned on desktop */}
          <div
            className={`flex flex-col gap-4 desktop:gap-8 w-full ${textWidthClass} text-center desktop:text-left items-center desktop:items-start`}
          >
            {blok.eyebrow && (
              <p className="text-callout font-extrabold uppercase tracking-[0.16em] text-brand-mid">
                {blok.eyebrow}
              </p>
            )}
            <h2
              id={headlineId}
              className={`text-h5 desktop:text-h4 font-extrabold ${detached ? 'text-brand-deep' : 'text-brand-blue'}`}
            >
              {blok.headline}
            </h2>
            {blok.body && (
              <div className="text-body2 desktop:text-body1 text-gray-700">
                <StoryblokRichText
                  doc={blok.body as unknown as Parameters<typeof StoryblokRichText>[0]['doc']}
                />
              </div>
            )}
            {blok.pillars && blok.pillars.length > 0 && (
              <div className="flex flex-col gap-6 w-full">
                {blok.pillars.map((pillar) => (
                  <StoryblokComponent key={pillar._uid} blok={pillar as unknown as SbBlokData} />
                ))}
              </div>
            )}
            {showCta && (
              <a
                href={ctaHref ?? undefined}
                className="inline-flex items-center justify-center px-6 h-12 rounded-md bg-brand-blue text-neutral-white font-bold transition-colors hover:bg-brand-deep"
              >
                {ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
